import { useEffect, useState } from 'react';
import { checkIfInqOnlyMode } from '../../Store/Reducers';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../Components/UI/dialog';
import { Button } from '../../Components/UI/button';
import { Label } from '../../Components/UI/label';
import { X, ArrowLeft, UserCheck } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getThreadTypesArr, getUsersArr } from '../../Store/Reducers';
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD';
import { useDesktop } from '../../Components/Common/IsDesktop';
import { getAuthData } from '../../Utils/AuthUtils';
import { getThreadNameFromTypeID } from '../../Utils/Utilities';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from '../../Components/UI/alert-dialog';

const ReassignDialog = ({
  open,
  onOpenChange,
  threadData,
  onReassign,
  selectedThreadCount = 1,
  isBulkReassign = false
}) => {
  const isDesktop = useDesktop();
  const authData = getAuthData();
  const users = useSelector(getUsersArr);
  const [selectedUserID, setSelectedUserID] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const threadTypesData = useSelector(getThreadTypesArr);
  const threadName = getThreadNameFromTypeID(threadData?.typeID, threadTypesData, "SnSng");
  
  const handleReassign = async () => {
    if (!selectedUserID || selectedUserID === '0' || selectedUserID === 0) {
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmReassign = async () => {
    await onReassign(selectedUserID);
    setConfirmDialogOpen(false);
    onOpenChange(false);
  };

  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  
  const userOptions = isBulkReassign
    ? users.map(user => ({
      value: String(user.userID),
      label: user.name
    })).sort((a, b) => {
      const currentUserID = authData?.UserID ? authData.UserID.toString() : '';
      if (a.value === currentUserID) return -1;
      if (b.value === currentUserID) return 1;
      return a.label.localeCompare(b.label);
    })
    : users
      .filter(user => user.userID !== threadData?.pocUserID)
      .map(user => ({
        value: String(user.userID),
        label: user.name
      })).sort((a, b) => {
        const currentUserID = authData?.UserID ? authData.UserID.toString() : '';
        if (a.value === currentUserID) return -1;
        if (b.value === currentUserID) return 1;
        return a.label.localeCompare(b.label);
      });
 
  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedUserID('');
    }
  }, [open]);

  const renderHeader = () => (
    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
      <div className="sm:hidden flex items-center justify-between w-full h-8">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
            <ArrowLeft className="h-6 w-6" />
            <span className="sr-only">Back</span>
          </DialogClose>
          <DialogTitle className="text-lg font-medium">
            Reassign {!isBulkReassign ? threadName : `${selectedThreadCount === 1 ? "Inquiry" : "Inquiries"}`}
          </DialogTitle>
        </div>
      </div>
      <DialogTitle className="hidden sm:block text-lg md:text-xl">
        Reassign {!isBulkReassign ? threadName : `${selectedThreadCount === 1 ? "Inquiry" : "Inquiries"}`}
      </DialogTitle>
      <div className="hidden sm:flex items-center gap-2">
        <DialogClose className="flex-shrink-0" tabIndex={-1}>
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </div>
    </DialogHeader>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col sm:!max-w-[360px] sm:min-h-0 sm:max-h-[95vh] sm:h-auto sm:rounded-lg rounded-none">
        {renderHeader()}

        <div className="pb-4 px-4 flex-1">
          <div className="space-y-4">
            {/* Reassign to field */}
            <div className="space-y-1.5">
              <Label htmlFor="reassignTo" required>
                {isBulkReassign 
                  ? `Reassign ${selectedThreadCount} ${selectedThreadCount == 1 ? "Inquiry" : "Inquiries"} to:` 
                  : <>Reassign from <strong>{users.find(u => u.userID === threadData?.pocUserID)?.name || 'Unknown'}</strong> to:</>
                }
              </Label>
              <CustomSearchableDropdown
                options={userOptions}
                value={selectedUserID}
                onSelect={(value) => setSelectedUserID(value)}
                placeholder="Select Executive"
                searchPlaceholder="Search users"
                displayKey="label"
                valueKey="value"
                allowAdd={false}
                label="Executive"
                emptyMessage="No users found."
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-start sm:gap-3 w-full">
            <div className="flex sm:hidden flex-col gap-3 w-full">
              <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    onClick={handleReassign}
                    disabled={!selectedUserID || selectedUserID === '0'}
                    className="sahi-primary-button w-full"
                  >
                    <UserCheck className="h-4 w-4" />
                    Reassign {isBulkReassign ? `${selectedThreadCount} ${selectedThreadCount == 1 ? "Inquiry" : "Inquiries"}` : threadName}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Confirm Reassignment
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to reassign {selectedThreadCount} {selectedThreadCount === 1 ? "Inquiry" : "Inquiries"} to <strong>{users.find(u => u.userID === Number(selectedUserID))?.name || 'Unknown'}</strong>?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="!justify-start gap-3">
                    <Button
                      onClick={handleConfirmReassign}
                      className={`sahi-primary-button flex-1 sm:flex-none sm:min-w-[120px]`}
                    >
                      <UserCheck className="w-3 h-3" />
                      Yes, Reassign
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setConfirmDialogOpen(false)}
                      className={`sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]`}
                    >
                      <X className="w-3 h-3" />
                      Cancel
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <div className="hidden sm:flex sm:gap-3">
              <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    onClick={handleReassign}
                    disabled={!selectedUserID || selectedUserID === '0'}
                    className="sahi-primary-button "
                  >
                    <UserCheck className="h-4 w-4" />
                    Reassign
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Confirm Reassignment
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to reassign {selectedThreadCount} {selectedThreadCount === 1 ? "Inquiry" : "Inquiries"} to <strong>{users.find(u => u.userID === Number(selectedUserID))?.name || 'Unknown'}</strong>?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="!justify-start gap-3">
                    <Button
                      onClick={handleConfirmReassign}
                      className={`sahi-primary-button flex-1 sm:flex-none sm:min-w-[120px]`}
                    >
                      <UserCheck className="w-3 h-3" />
                      Yes, Reassign
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setConfirmDialogOpen(false)}
                      className={`sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]`}
                    >
                      <X className="w-3 h-3" />
                      Cancel
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="sahi-secondary-button "
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReassignDialog;